import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Typography } from 'antd';
import { AuthContext, AuthContextState } from '../../context/AuthContext';
import LoginForm, { LoginCredentials } from '../../components/LoginForm';
import MainLayout from '../../components/MainLayout';
import StatusMessages, { StatusMessage } from '../../components/StatusMessages';
import { DASHBOARD_ROUTE } from '../../config/constants';
import styles from './Login.module.css';

export interface LoginProps {
  // status messages to display on top
  // can be passed through props or through state
  statusMessages?: StatusMessage[]
};

// user login page
const Login = (props: LoginProps): JSX.Element | null => {
  // get state passed by previous location
  const { state } = useLocation();
  // status messages to display on top
  const [statusMessages, setStatusMessages] = useState<StatusMessage[]>(
    // get status messages from props
    // if undefined, get status messages from state passed by previous location
    // otherwise leave empty
    props.statusMessages ?? state?.statusMessages ?? []
  );
  const navigate = useNavigate();
  // get authenticate function from auth context
  const { authenticateUser, checkAuthenticated, fetchMe } = useContext(AuthContext) as AuthContextState;
  // get url search params
  const [searchParams] = useSearchParams();

  // set document title
  useEffect(() => {
    document.title = 'Login | Sigmoid Academy';
  }, []);

  // handle user login
  const handleLogin = async (credentials: LoginCredentials): Promise<void> => {
    // clear status messages
    setStatusMessages((previousMessages: StatusMessage[]) => []);
    // authenticate using provided credentials
    try {
      await authenticateUser(
        credentials.usernameOrEmail,
        credentials.password
      );
      await checkAuthenticated();
      await fetchMe();

      const nextURL = searchParams.get('next');
      if (nextURL) {
        navigate(nextURL);
      }
      else {
        navigate(DASHBOARD_ROUTE);
      }
    } catch(error: any) {
      if (error.response?.data !== undefined) {
        // set danger status message on login failure
        setStatusMessages((previousMessages: StatusMessage[]) => [
          ...previousMessages,
          {
            message: (error.response?.data as any).detail,
            status: 'error'
          }
        ]);
      }
    };
  };

  return (
    <div>
      <MainLayout selectedKey='login'>
        <div className={styles.mainContainer}>
          <Typography.Title
            level={1}
            className={styles.mainHeading}
          >
            Login
          </Typography.Title>
          <StatusMessages items={statusMessages} />
          <LoginForm handleLogin={handleLogin} />
        </div>
      </MainLayout>
    </div>
  );
};

export default Login;
